import { useEffect, useId, useRef, useState } from 'react'
import { X } from 'lucide-react'
import type { ScoringRules } from '../../facilities/index.js'
import type { ShiftOutcome, ShiftOutcomeKind } from '../../partner/index.js'
import { OutcomeRow } from './OutcomeLog.js'
import { standingOf, type DayCell, type DayStanding, type MonthCalendarData } from './monthPanelData.js'

export type DayRules = Pick<ScoringRules, 'graceMinutes' | 'shortNoticeHours'>

const KIND_LABELS: Record<ShiftOutcomeKind, string> = { completed: 'Completed', callout: 'Call-out', no_show: 'No-show' }

/** Green counts for the worker; gold is a shift worked but not cleanly; coral counts against, filled when it counts most. `short` fits the legend under a small calendar. */
const STANDING_PRESENTATION: Record<DayStanding, { label: string; short: string; className: string }> = {
  for: { label: 'On time, worked to the end', short: 'On time', className: 'bg-green text-white' },
  partly: { label: 'Late or left early', short: 'Late or early', className: 'bg-gold text-ink' },
  against: { label: 'Call-out with notice', short: 'Call-out', className: 'bg-coral-lt text-coral' },
  severe: { label: 'Short-notice call-out or no-show', short: 'Short notice / no-show', className: 'bg-coral text-white' },
}

const STANDINGS: readonly DayStanding[] = ['for', 'partly', 'against', 'severe']
const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const

function formatTime(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function formatDay(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/** One shift in words: what happened, when, and how it counted - so the colour is never the only carrier of the meaning. */
function describeShift(outcome: ShiftOutcome, rules: DayRules): string {
  const scheduled = `${formatTime(outcome.scheduledStart)}–${formatTime(outcome.scheduledEnd)}`
  const standing = STANDING_PRESENTATION[standingOf(outcome, rules)].label.toLowerCase()
  if (outcome.kind === 'completed' && outcome.actualStart && outcome.actualEnd) {
    return `${KIND_LABELS.completed}, worked ${formatTime(outcome.actualStart)}–${formatTime(outcome.actualEnd)} of ${scheduled} (${standing})`
  }
  if (outcome.kind === 'callout' && outcome.reportedAt) {
    return `${KIND_LABELS.callout} for ${scheduled}, notice given ${new Date(outcome.reportedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} (${standing})`
  }
  return `${KIND_LABELS[outcome.kind]} for ${scheduled} (${standing})`
}

/** What a day's cell says on hover and to a screen reader: the date, then each shift. */
function describeDay(cell: DayCell, rules: DayRules): string {
  return cell.outcomes.length === 0 ? `${formatDay(cell.date)}: no shift reported` : `${formatDay(cell.date)}: ${cell.outcomes.map((outcome) => describeShift(outcome, rules)).join('; ')}`
}

/**
 * The detail for one day, as a small modal over the shifts panel: its date
 * and how it counted, then each shift as a row. Centered on the panel
 * rather than the page, so it reads as part of the calendar it came from.
 * Dismissed by its close button, Escape, or a click anywhere outside it.
 */
function DayModal({ cell, id, onClose }: { cell: DayCell; id: string; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const standing = cell.standing ? STANDING_PRESENTATION[cell.standing] : null

  useEffect(() => {
    closeRef.current?.focus()
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    function onPointerDown(event: MouseEvent): void {
      if (dialogRef.current && event.target instanceof Node && !dialogRef.current.contains(event.target)) {
        onClose()
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [onClose])

  return (
    <>
      {/* The backdrop dims the panel the modal belongs to; a click on it is a click outside the dialog. */}
      <div aria-hidden="true" className="absolute inset-0 z-10 bg-ink/20" />
      <div
        ref={dialogRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={`Shifts on ${formatDay(cell.date)}`}
        className="absolute left-1/2 top-1/2 z-20 w-[min(320px,90vw)] -translate-x-1/2 -translate-y-1/2 bg-white border border-marine/30 shadow-xl"
      >
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-marine/10">
          <span className="text-[11px] font-bold text-ink">{formatDay(cell.date)}</span>
          {standing && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${standing.className}`}>{standing.label}</span>}
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="ml-auto text-ink-mid hover:text-ink">
            <X aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </div>
        <ul className="divide-y divide-marine/10">
          {cell.outcomes.map((outcome) => (
            <OutcomeRow key={outcome.eventId} outcome={outcome} />
          ))}
        </ul>
      </div>
    </>
  )
}

interface MonthCalendarProps {
  calendar: MonthCalendarData
  rules: DayRules
  id?: string
}

/**
 * A month's days in weekday columns, each shaded by how its shifts counted
 * - the size and spacing of a date picker, so it sits inside a panel or a
 * card. A day with a shift is a button: pressing it opens a small modal
 * centered on the calendar with that day's shifts in full, dismissed by
 * its close button, Escape or a click outside. The full wording of every
 * day is also on hover and in its label; the legend keeps to the short
 * forms. Give it a `key` of the month, so a chosen day does not carry over
 * to another month.
 */
export function MonthCalendar({ calendar, rules, id }: MonthCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const modalId = `${useId()}-day`
  const selected = calendar.days.find((cell) => cell.date === selectedDate) ?? null

  return (
    <div id={id} className="relative">
      <div className="grid grid-cols-7 gap-px text-center">
        {WEEKDAY_INITIALS.map((initial, index) => (
          <div key={index} className="h-4 text-[8px] font-bold leading-4 text-ink-mid">
            {initial}
          </div>
        ))}
        {Array.from({ length: calendar.leadingBlanks }, (_, index) => (
          <div key={`blank-${index}`} aria-hidden="true" />
        ))}
        {calendar.days.map((cell) => {
          const standing = cell.standing ? STANDING_PRESENTATION[cell.standing] : null
          const label = describeDay(cell, rules)
          if (!standing) {
            return (
              <div key={cell.day} role="img" aria-label={label} title={label} className="h-5 text-[9px] leading-5 rounded-sm text-ink-mid/70">
                {cell.day}
              </div>
            )
          }
          const isSelected = selected?.date === cell.date
          return (
            <button
              key={cell.day}
              type="button"
              aria-label={label}
              aria-expanded={isSelected}
              aria-controls={isSelected ? modalId : undefined}
              title={label}
              onClick={() => setSelectedDate(isSelected ? null : cell.date)}
              className={`h-5 text-[9px] leading-5 rounded-sm font-bold ${standing.className} ${isSelected ? 'ring-2 ring-marine ring-offset-1' : 'hover:ring-1 hover:ring-marine/50'}`}
            >
              {cell.day}
            </button>
          )
        })}
      </div>
      <ul className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5">
        {STANDINGS.map((standing) => (
          <li key={standing} className="flex items-center gap-1 text-[8.5px] leading-tight text-ink-mid" title={STANDING_PRESENTATION[standing].label}>
            <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 rounded-sm ${STANDING_PRESENTATION[standing].className}`} />
            {STANDING_PRESENTATION[standing].short}
          </li>
        ))}
      </ul>
      {selected && <DayModal cell={selected} id={modalId} onClose={() => setSelectedDate(null)} />}
    </div>
  )
}
